import {
    type ContractVariant,
    widgetPunctualityKpisectionContract,
} from 'aos-ds/src/patterns/widget-punctuality-kpisection/widget-punctuality-kpisection.contract'
import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import type { PunctualityDirectionKey } from '../punctuality-direction-label'
import {
    PUNCTUALITY_KPI_DIRECTION_LAYOUTS,
    PunctualityKpiDirection,
    type PunctualityKpiDirectionData,
    type PunctualityKpiDirectionLayout,
    withoutSampleWidth,
} from '../punctuality-kpi-direction'

export const PUNCTUALITY_KPI_SECTION_SIZES = widgetPunctualityKpisectionContract.props.size.values
export const PUNCTUALITY_KPI_SECTION_DETAILED =
    widgetPunctualityKpisectionContract.props.detailed.values

export type PunctualityKpiSectionSize = (typeof PUNCTUALITY_KPI_SECTION_SIZES)[number]
export type PunctualityKpiSectionDetailed = (typeof PUNCTUALITY_KPI_SECTION_DETAILED)[number]

export type PunctualityKpiSectionProps = {
    size: PunctualityKpiSectionSize
    detailed: PunctualityKpiSectionDetailed
    departures: PunctualityKpiDirectionData
    arrivals: PunctualityKpiDirectionData
    className?: string
    testID?: string
}

export const punctualityKpiSectionKey = (
    size: PunctualityKpiSectionSize,
    detailed: PunctualityKpiSectionDetailed,
): string => `size=${size}, detailed=${detailed}`

const cells = widgetPunctualityKpisectionContract.variants as Record<string, ContractVariant>

export const PUNCTUALITY_KPI_SECTION_CELLS = Object.keys(cells).map(key => {
    const [size, detailed] = key.replace('size=', '').split(', detailed=')
    return {
        size: size as PunctualityKpiSectionSize,
        detailed: detailed as PunctualityKpiSectionDetailed,
    }
})

export const resolvePunctualityKpiSection = (
    size: PunctualityKpiSectionSize,
    detailed: PunctualityKpiSectionDetailed,
): ContractVariant => {
    const key = punctualityKpiSectionKey(size, detailed)
    const cell = cells[key]
    if (!cell) {
        throw new Error(`PunctualityKpiSection: the Figma contract has no "${key}"`)
    }
    return cell
}

const partOf = (cell: ContractVariant, name: string): string => {
    const found = cell.parts[name]
    if (found === undefined) {
        throw new Error(`PunctualityKpiSection: the Figma contract has no part "${name}"`)
    }
    return found
}

const KPI_DIRECTION = 'Widget/Punctuality/KpiDirection'

export const directionLayoutOf = (cell: ContractVariant): PunctualityKpiDirectionLayout => {
    const composed = cell.composes.find(item => item.component === KPI_DIRECTION)
    const layouts = [
        ...new Set((composed?.variants ?? []).map(variant => variant.split('layout=')[1])),
    ]
    const [layout] = layouts
    if (layouts.length !== 1 || !PUNCTUALITY_KPI_DIRECTION_LAYOUTS.includes(layout as never)) {
        throw new Error(`PunctualityKpiSection: no single ${KPI_DIRECTION} layout in the cell`)
    }
    return layout as PunctualityKpiDirectionLayout
}

/**
 * Figma `Widget/Punctuality/KpiSection` (18906:122099): Departures and Arrivals
 * side by side. `tile`, `S` and `M` hold the two columns in a `cols` row; `L`
 * sets them in the section's own row with a 1px divider between them. Gaps,
 * the divider and the column layout come from the contract; the frame width is
 * the sample Figma drew, and the section fills its widget.
 *
 * The `delayed` counts `L` draws (`Widget/Stat/CountLabel`) have no contract
 * and no data, so they are not drawn. Since D6 (2026-10-29) only `tile` is
 * `detailed=off`; the old S / M / L layout is kept in Figma `19908:474233`.
 */
export const PunctualityKpiSection = ({
    size,
    detailed,
    departures,
    arrivals,
    className,
    testID = 'punctuality-kpi-section',
}: PunctualityKpiSectionProps) => {
    const cell = resolvePunctualityKpiSection(size, detailed)
    const layout = directionLayoutOf(cell)
    const container = withoutSampleWidth(cell.container)

    const direction = (key: PunctualityDirectionKey, data: PunctualityKpiDirectionData) => (
        <PunctualityKpiDirection
            {...data}
            direction={key}
            layout={layout}
            className={partOf(cell, key)}
            testID={`${testID}-${key}`}
        />
    )

    if (cell.parts.cols === undefined) {
        return (
            <View className={twMerge('w-full flex-row', container, className)} testID={testID}>
                {direction('departures', departures)}
                <View
                    className={twMerge('self-stretch', partOf(cell, 'divider'))}
                    testID={`${testID}-divider`}
                />
                {direction('arrivals', arrivals)}
            </View>
        )
    }

    return (
        <View className={twMerge('w-full', container, className)} testID={testID}>
            <View className={twMerge('w-full flex-row', partOf(cell, 'cols'))}>
                {direction('departures', departures)}
                {direction('arrivals', arrivals)}
            </View>
        </View>
    )
}

PunctualityKpiSection.displayName = 'PunctualityKpiSection'
